<template>
  <RevForm
    class="md:m-auto md:w-1/2"
    :initial-values
    :name="FORM_NAME"
    :validate="validate()"
    @submit="handleSubmit"
  >
    <template #default="{ values, errors, hasError }">
      <h2 class="heading-2 my-16">
        {{ i18n(translations.newPasswordTitle) }}
      </h2>
      <RevInputPassword
        id="newPassword"
        v-model="values.newPassword"
        autocomplete="new-password"
        class="mx-auto mb-14 mt-24 text-left"
        :error="errors.newPassword"
        :hide-aria-label="i18n(translations.passwordButtonHideAriaLabel)"
        :label="i18n(translations.newPassword)"
        required
        :show-aria-label="i18n(translations.passwordButtonShowAriaLabel)"
      />

      <h2 class="heading-2 my-16">
        {{ i18n(translations.passwordConfirmTitle) }}
      </h2>
      <RevInputPassword
        id="newPasswordConfirmation"
        v-model="values.newPasswordConfirmation"
        autocomplete="new-password"
        class="mx-auto mb-32 mt-16 text-left"
        :error="errors.newPasswordConfirmation"
        :hide-aria-label="i18n(translations.passwordButtonHideAriaLabel)"
        :label="i18n(translations.passwordConfirm)"
        required
        :show-aria-label="i18n(translations.passwordButtonShowAriaLabel)"
      />
      <RevButton
        id="submitPasswordReset"
        :disabled="isSubmitDisabled(hasError)"
        full-width="always"
        :loading
        type="submit"
        variant="primary"
      >
        {{ i18n(translations.submit) }}
      </RevButton>
    </template>
  </RevForm>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { ref } from 'vue'

import { HttpApiError, handleValidationErrors } from '@backmarket/http-api'
import { useResetPassword } from '@backmarket/nuxt-layer-oauth/useResetPassword'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEqual } from '@backmarket/utils/object/isEqual'
import { RevButton } from '@ds/components/Button'
import {
  FORM_VALID,
  RevForm,
  makeValidate,
  matchingRegExp,
  maxLength,
  required,
  setFormErrors,
} from '@ds/components/Form'
import { RevInputPassword } from '@ds/components/InputPassword'

import {
  VALIDATION_ERROR,
  VALID_PASSWORD_REGEXP,
} from '~/scopes/auth/constants'
import { ROUTES } from '~/scopes/auth/route-names'

import translations from './SetNewPasswordForm.translations'
import type { FormValues } from './SetNewPasswordForm.types'

const FORM_NAME = 'setNewPassword'

const i18n = useI18n()
const route = useRoute()
const tracking = useTracking()
const { resetPassword } = useResetPassword()

const initialValues = ref({
  newPassword: '',
  newPasswordConfirmation: '',
})
const loading = ref(false)

const { openErrorToast } = useTheToast()

function validate() {
  const requiredMessage = i18n(translations.requiredFieldError)
  const passwordDifferenceMessage = i18n(translations.passwordEqualityError)
  const passwordStrengthMessage = i18n(translations.passwordStrength)
  const passwordTooLongMessage = i18n(translations.passwordTooLong)

  return makeValidate<FormValues>({
    newPassword: [
      required(requiredMessage),
      maxLength(64, passwordTooLongMessage),
      matchingRegExp(VALID_PASSWORD_REGEXP, passwordStrengthMessage),
    ],
    newPasswordConfirmation: [
      required(requiredMessage),
      function passwordEquality(newPasswordConfirmation, { newPassword }) {
        return isEqual(newPassword, newPasswordConfirmation)
          ? FORM_VALID
          : passwordDifferenceMessage
      },
    ],
  })
}

function isSubmitDisabled(hasError: boolean | undefined) {
  return hasError || loading.value
}

async function handleSubmit({
  newPassword,
  newPasswordConfirmation,
}: {
  newPassword: string
  newPasswordConfirmation: string
}) {
  loading.value = true

  try {
    await resetPassword(
      route.params.userId as string,
      route.params.token as string,
      newPassword,
      newPasswordConfirmation,
    )

    tracking.trackResetPasswordSuccess()

    await navigateTo({
      name: ROUTES.AUTH.LOGIN,
    })
  } catch (error) {
    const apiError = HttpApiError.fromAnyError(error as Error)

    if (apiError.type === VALIDATION_ERROR) {
      handleValidationErrors<FormValues>({
        error: apiError,
        errorsToHandleConfigs: [
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/required',
            formField: 'newPassword',
            errorMessage: i18n(translations.requiredFieldError),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/invalid-password',
            formField: 'newPassword',
            errorMessage: i18n(translations.invalidPassword),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-strength',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordStrength),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-too-similar',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordTooSimilar),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-too-common',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordTooCommon),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-too-long',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordTooLong),
          },
          {
            kind: 'FIELD',
            target: 'new_password',
            type: '/errors/password-reuse',
            formField: 'newPassword',
            errorMessage: i18n(translations.passwordReuse),
          },
          {
            kind: 'FIELD',
            target: 'new_password_confirmation',
            type: '/errors/required',
            formField: 'newPasswordConfirmation',
            errorMessage: i18n(translations.requiredFieldError),
          },
        ],
        setFieldErrors: (errors) => {
          setFormErrors(FORM_NAME, errors)
        },
        setToastError: (toastParams) => {
          openErrorToast(toastParams)
        },
      })

      return
    }

    openErrorToast()
  } finally {
    loading.value = false
  }
}
</script>
